import {
  blockiert,
  luecken,
  stundeAus,
  type Block,
  type Kalendertermin,
  type Tagesplan,
} from '../meintag/tagesplan'

/**
 * **Der Tagesstrom von „My Work"** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md) AP 24.3).
 *
 * Eine Zeitachse von oben nach unten: was vorbei ist (eine Zeile), das Jetzt, dann Termine und
 * freie **Fenster** im Wechsel bis zum Arbeitsende. In den Fenstern liegt, was der Planer
 * ({@link Tagesplan} aus `tagBelegen`) dort hineingelegt hat – Laufendes, ☀ Markiertes, Vorschläge.
 *
 * Gerechnet wird in **Stunden seit Mitternacht** wie im Planer: Die Termine kommen als Ortszeit
 * ohne Versatz (`stundeAus`), die Blöcke des Plans ebenso. So passen beide ohne Umrechnung
 * aufeinander, und kein Sommerzeit-Versatz kann einen Termin neben sein Fenster schieben.
 *
 * Reine Funktion, ohne React – getestet in `strom.test.ts`.
 */

export interface StromTermin extends Kalendertermin {
  id: string
  abgesagt: boolean
  ort?: string | null
  webLink?: string | null
  teilnehmer?: readonly string[]
  /** Gleiche Reihenfolge wie `teilnehmer` (Z-24 AP 24.14) – daran erkennt Work seine Leute. */
  teilnehmerAdressen?: readonly string[]
  serie?: string | null
}

export type StromEintrag =
  | { art: 'vorbei'; termine: StromTermin[] }
  | {
      art: 'jetzt'
      um: number
      /** Der Termin, der gerade läuft (blockierend) – sonst `null`. */
      laufend: StromTermin | null
      /** Bis wann das Fenster reicht, in dem das Jetzt liegt – `null` in einem Termin. */
      fensterBis: number | null
    }
  | { art: 'termin'; termin: StromTermin; von: number; bis: number; laufend: boolean }
  | {
      art: 'fenster'
      von: number
      bis: number
      /** Was der Planer hineingelegt hat – ohne Grundlast, in Reihenfolge. */
      bloecke: Block[]
      /** Arbeitszeit im Fenster: Länge minus Grundlast. */
      laenge: number
      /** Was davon nach den gelegten Aufgaben übrig ist. */
      frei: number
      /** Liegt das Jetzt in diesem Fenster? Dann beginnt es beim Jetzt. */
      jetztDrin: boolean
    }
  | { art: 'ende'; um: number }

export interface Strom {
  eintraege: StromEintrag[]
  /** Ganztägiges steht nicht im Strom, sondern als Zeile im Kopf. */
  ganztaegig: StromTermin[]
  /** Termine, die noch kommen oder gerade laufen (nur blockierende). */
  kommendeTermine: number
  /** Fenster ab jetzt. */
  fenster: number
  /** Arbeitszeit in den Fenstern ab jetzt, ohne Grundlast. */
  freiStunden: number
}

const EPS = 1e-6
const AUFGABEN_ARTEN = new Set<Block['art']>(['aufgabe', 'markiert', 'fest', 'vorschlag'])

const ueberlappung = (a: { von: number; bis: number }, von: number, bis: number) =>
  Math.max(0, Math.min(a.bis, bis) - Math.max(a.von, von))

export function stromBauen({
  termine,
  plan,
  jetzt,
  arbeitVon,
  arbeitBis,
}: {
  termine: readonly StromTermin[]
  plan: Pick<Tagesplan, 'bloecke'>
  /** Stunden seit Mitternacht, z. B. 10,78 für 10:47. */
  jetzt: number
  /** Beginn und Ende der Arbeitszeit des Tages (frühestes und spätestes Fenster). */
  arbeitVon: number
  arbeitBis: number
}): Strom {
  const gueltig = termine.filter((t) => !t.abgesagt)
  const ganztaegig = gueltig.filter((t) => t.ganztaegig)
  const zeitlich = gueltig
    .filter((t) => !t.ganztaegig)
    .flatMap((t) => {
      const von = stundeAus(t.beginn)
      const bis = stundeAus(t.ende)
      return von === null || bis === null || bis <= von ? [] : [{ t, von, bis }]
    })
    .sort((a, b) => a.von - b.von || a.bis - b.bis)

  const vorbei = zeitlich.filter((x) => x.bis <= jetzt + EPS)
  const kommend = zeitlich.filter((x) => x.bis > jetzt + EPS)
  const laufend = kommend.find((x) => x.von <= jetzt + EPS && blockiert(x.t)) ?? null

  /** Freie Spannen ab jetzt – dieselbe Rechnung wie im Planer (`luecken`). */
  const ab = Math.max(jetzt, arbeitVon)
  const spannen =
    ab < arbeitBis - EPS
      ? luecken(
          kommend.map((x) => x.t),
          { von: ab, bis: arbeitBis },
        )
      : []

  const fenster: Extract<StromEintrag, { art: 'fenster' }>[] = spannen.map((s) => {
    const drin = plan.bloecke.filter((b) => ueberlappung(b, s.von, s.bis) > EPS)
    const grundlast = drin
      .filter((b) => b.art === 'grundlast')
      .reduce((n, b) => n + ueberlappung(b, s.von, s.bis), 0)
    const aufgaben = drin.filter((b) => AUFGABEN_ARTEN.has(b.art))
    const belegt = aufgaben.reduce((n, b) => n + ueberlappung(b, s.von, s.bis), 0)
    const laenge = Math.max(0, s.bis - s.von - grundlast)
    return {
      art: 'fenster',
      von: s.von,
      bis: s.bis,
      bloecke: aufgaben.sort((a, b) => a.von - b.von),
      laenge,
      frei: Math.max(0, laenge - belegt),
      jetztDrin: s.von <= jetzt + EPS,
    }
  })

  const terminEintraege: Extract<StromEintrag, { art: 'termin' }>[] = kommend.map((x) => ({
    art: 'termin',
    termin: x.t,
    von: x.von,
    bis: x.bis,
    laufend: x.von <= jetzt + EPS,
  }))

  const folge: Extract<StromEintrag, { art: 'termin' | 'fenster' }>[] = [
    ...terminEintraege,
    ...fenster,
  ].sort((a, b) => a.von - b.von || (a.art === 'termin' ? -1 : 1))

  const eintraege: StromEintrag[] = []
  if (vorbei.length) eintraege.push({ art: 'vorbei', termine: vorbei.map((x) => x.t) })
  const jetztFenster = fenster.find((f) => f.jetztDrin)
  eintraege.push({
    art: 'jetzt',
    um: jetzt,
    laufend: laufend?.t ?? null,
    fensterBis: laufend ? null : (jetztFenster?.bis ?? null),
  })
  eintraege.push(...folge)
  if (arbeitBis > jetzt + EPS) eintraege.push({ art: 'ende', um: arbeitBis })

  return {
    eintraege,
    ganztaegig,
    kommendeTermine: kommend.filter((x) => blockiert(x.t)).length,
    fenster: fenster.length,
    freiStunden: fenster.reduce((n, f) => n + f.laenge, 0),
  }
}

/**
 * **Passt das Vorgenommene?** Für den Satz im Kopf und den Balken in „☀ Vorgenommen".
 *
 * Unter 85 % der freien Zeit „passt", bis 100 % „knapp", darüber „zu viel". Die 85 % sind
 * Puffer: Schätzungen liegen erfahrungsgemäß eher zu niedrig als zu hoch.
 */
export function passtUrteil(geplant: number, frei: number): 'passt' | 'knapp' | 'zuviel' | 'leer' {
  if (geplant <= EPS) return 'leer'
  if (frei <= EPS) return 'zuviel'
  const anteil = geplant / frei
  if (anteil <= 0.85) return 'passt'
  if (anteil <= 1 + EPS) return 'knapp'
  return 'zuviel'
}

/** `10:47` aus 10,78. */
export function uhrAus(h: number): string {
  const min = Math.round(h * 60)
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
}

/** `30 min`, `1 h`, `1,5 h` – kurz, wie im Kalender-Modus. */
export function dauerText(h: number): string {
  if (h <= EPS) return '0 min'
  if (h < 1 - EPS) return `${Math.max(5, Math.round((h * 60) / 5) * 5)} min`
  return `${(Math.round(h * 10) / 10).toLocaleString('de-DE')} h`
}

/** Ein Stück des Satzes im Kopf – `fett` für die Zahlen, auf die es ankommt. */
export interface SatzTeil {
  text: string
  fett?: boolean
}

/**
 * **Der Satz im Kopf** – der Tag in einer Zeile, wie im Heute des Notizbuchs.
 * „Guten Morgen, Nils. Noch 4 Termine und 2 Fenster mit 3,6 h frei – deine 5 vorgenommenen
 * passen knapp."
 */
export function kopfSatz({
  vorname,
  stunde,
  strom,
  laufend,
  restMinuten,
  vorgenommen,
  urteil,
  arbeitVorbei = false,
}: {
  vorname: string | null
  stunde: number
  strom: Pick<Strom, 'kommendeTermine' | 'fenster' | 'freiStunden'>
  laufend: string | null
  restMinuten: number
  /** Für heute markiert und noch offen. */
  vorgenommen: number
  urteil: ReturnType<typeof passtUrteil>
  /** Nach dem Ende der Arbeitszeit – dann geht es um den Abschluss, nicht um Fenster. */
  arbeitVorbei?: boolean
}): SatzTeil[] {
  const gruss = stunde < 11 ? 'Guten Morgen' : stunde < 18 ? 'Hallo' : 'Guten Abend'
  const teile: SatzTeil[] = [{ text: `${gruss}${vorname ? `, ${vorname}` : ''}. ` }]
  if (laufend) {
    teile.push({ text: laufend, fett: true }, { text: ` läuft noch ${restMinuten} min. ` })
  }

  if (arbeitVorbei && !laufend) {
    if (vorgenommen > 0) {
      teile.push(
        { text: 'Die Arbeitszeit ist vorbei, ' },
        { text: `${vorgenommen} vorgenommene`, fett: true },
        { text: ' sind noch offen – Zeit, den Tag abzuschließen.' },
      )
    } else {
      teile.push({ text: 'Die Arbeitszeit ist vorbei – Zeit für den Feierabend.' })
    }
    return teile
  }

  const n = strom.kommendeTermine - (laufend ? 1 : 0)
  if (n <= 0 && strom.fenster === 0) {
    teile.push({ text: laufend ? 'Danach keine Termine mehr. ' : 'Heute keine Termine mehr. ' })
  } else {
    teile.push({ text: laufend ? 'Danach ' : 'Noch ' })
    if (n > 0) teile.push({ text: `${n} ${n === 1 ? 'Termin' : 'Termine'}`, fett: true })
    if (strom.fenster > 0) {
      if (n > 0) teile.push({ text: ' und ' })
      teile.push(
        { text: `${strom.fenster} Fenster`, fett: true },
        { text: ` mit ${dauerText(strom.freiStunden)} frei. ` },
      )
    } else {
      teile.push({ text: ', dazwischen kein freies Fenster mehr. ' })
    }
  }

  if (vorgenommen === 0) {
    if (strom.fenster > 0) {
      teile.push({ text: 'Nichts vorgenommen – in den Fenstern stehen Vorschläge.' })
    }
  } else {
    const wie =
      urteil === 'passt' ? 'passen' : urteil === 'knapp' ? 'passen knapp' : 'passen nicht alle'
    teile.push(
      { text: 'Deine ' },
      { text: `${vorgenommen} vorgenommenen`, fett: true },
      { text: ` ${wie}.` },
    )
  }
  return teile
}
